'use client';

import { Store, Package, ArrowUpDown, Check } from 'lucide-react';
import { cn, formatCategory } from '@/lib/utils';
import { haptics } from '@/lib/native';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type MarketplaceView = 'stores' | 'products';

export const STORE_SORTS = [
  { key: 'recommended', label: 'Recommended' },
  { key: 'rating', label: 'Top rated' },
  { key: 'products', label: 'Most products' },
  { key: 'name', label: 'Name A–Z' },
] as const;

export const PRODUCT_SORTS = [
  { key: 'newest', label: 'Newest' },
  { key: 'price_asc', label: 'Price: low to high' },
  { key: 'price_desc', label: 'Price: high to low' },
  { key: 'name', label: 'Name A–Z' },
] as const;

export type StoreSort = (typeof STORE_SORTS)[number]['key'];
export type ProductSort = (typeof PRODUCT_SORTS)[number]['key'];
export type MarketplaceSort = StoreSort | ProductSort;

export function sortsForView(view: MarketplaceView) {
  return view === 'stores' ? STORE_SORTS : PRODUCT_SORTS;
}

export function defaultSortForView(view: MarketplaceView): MarketplaceSort {
  return view === 'stores' ? 'recommended' : 'newest';
}

/** Keeps a sort value valid when the view flips (the two lists barely overlap). */
export function coerceSort(view: MarketplaceView, sort: string | null): MarketplaceSort {
  const allowed = sortsForView(view).map((s) => s.key as string);
  return allowed.includes(sort ?? '') ? (sort as MarketplaceSort) : defaultSortForView(view);
}

export interface CategoryOption {
  key: string;
  count: number;
}

interface FilterBarProps {
  view: MarketplaceView;
  onViewChange: (view: MarketplaceView) => void;
  storeCount: number;
  productCount: number;
  categories: CategoryOption[];
  activeCategory: string | null;
  onCategoryChange: (category: string | null) => void;
  sort: MarketplaceSort;
  onSortChange: (sort: MarketplaceSort) => void;
}

/**
 * Secondary header bar for the marketplace: category filters + sort on the
 * left, the Stores/Products view toggle pinned right.
 *
 * Sticks directly under the app's top bar — on phones that bar is fixed, so
 * the offset is `--app-top-inset`; on desktop it is in flow and the offset is
 * 0. Every control writes to the URL (see the page), which keeps a filtered
 * view shareable and survives the top bar's search submit.
 */
export function MarketplaceFilterBar({
  view,
  onViewChange,
  storeCount,
  productCount,
  categories,
  activeCategory,
  onCategoryChange,
  sort,
  onSortChange,
}: FilterBarProps) {
  const sorts = sortsForView(view);
  const activeSortLabel = sorts.find((s) => s.key === sort)?.label ?? sorts[0].label;
  const totalCount = view === 'stores' ? storeCount : productCount;

  const pickCategory = (key: string | null) => {
    if (key === activeCategory) return;
    void haptics.selection();
    onCategoryChange(key);
  };

  const views: { key: MarketplaceView; label: string; icon: typeof Store }[] = [
    { key: 'stores', label: 'Stores', icon: Store },
    { key: 'products', label: 'Products', icon: Package },
  ];
  const activeViewIndex = views.findIndex((v) => v.key === view);

  return (
    <div className="sticky top-[var(--app-top-inset)] md:top-0 z-30 ios-bar ios-bar-top">
      <div className="flex items-center gap-2 px-4 py-2 md:px-6">
        {/* Filters — horizontally scrollable so the toggle never gets pushed off. */}
        <div className="chip-rail no-scrollbar flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto pr-3">
          <FilterChip active={!activeCategory} onClick={() => pickCategory(null)}>
            All
            <span className="ml-1 tabular-nums opacity-60">{totalCount}</span>
          </FilterChip>

          {categories.map((c) => (
            <FilterChip
              key={c.key}
              active={activeCategory === c.key}
              onClick={() => pickCategory(c.key)}
            >
              {formatCategory(c.key)}
              <span className="ml-1 tabular-nums opacity-60">{c.count}</span>
            </FilterChip>
          ))}
        </div>

        {/* Sort */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={() => void haptics.light()}
              className="inline-flex h-8 shrink-0 items-center gap-1 rounded-full border border-border bg-background px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
              aria-label={`Sort: ${activeSortLabel}`}
            >
              <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
              <span className="hidden lg:inline">{activeSortLabel}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {sorts.map((s) => (
              <DropdownMenuItem
                key={s.key}
                onSelect={() => {
                  if (s.key === sort) return;
                  void haptics.selection();
                  onSortChange(s.key);
                }}
                className="flex items-center justify-between gap-2 text-sm"
              >
                {s.label}
                {s.key === sort && <Check className="h-4 w-4 text-primary" aria-hidden />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* View toggle — a two-slot segmented control with a sliding thumb. */}
        <div
          role="tablist"
          aria-label="Marketplace view"
          className="relative grid shrink-0 grid-cols-2 rounded-lg bg-muted p-0.5"
        >
          <span
            aria-hidden="true"
            className="absolute inset-y-0.5 left-0.5 w-[calc(50%-0.125rem)] rounded-md bg-background shadow-sm transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
            style={{ transform: `translateX(${activeViewIndex * 100}%)` }}
          />
          {views.map((v) => {
            const isActive = v.key === view;
            return (
              <button
                key={v.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => {
                  if (isActive) return;
                  void haptics.selection();
                  onViewChange(v.key);
                }}
                className={cn(
                  'relative z-10 inline-flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-medium transition-colors sm:px-2.5 sm:text-xs',
                  isActive ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                <v.icon className="h-3.5 w-3.5" aria-hidden />
                {v.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex h-8 shrink-0 items-center rounded-full border px-3 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background text-foreground hover:bg-muted',
      )}
    >
      {children}
    </button>
  );
}
